import { flexCenter, flexColumn, transitionMixin } from "@components/mixins";
import React, { useState } from "react";
import styled from "styled-components";
import headersHelper from "./../../utils/newsletterHelpers";

const Container = styled.div`
    ${flexColumn};
    text-align: center;
    margin: 50px 0;
    padding: 40px;
    border: 1px solid ${({ theme }) => theme.colors.black};

    h6 {
        margin: 15px 0;

        &.red {
            color: ${({ theme }) => theme.colors.red};
        }

        &.message {
            animation: fadeIn 1s both;

            @keyframes fadeIn {
                from {
                    transform: translateX(15px);
                    opacity: 0;
                }
                to {
                    transform: translateX(0px);
                    opacity: 1;
                }
            }
        }
    }

    .privacy-policy {
        font-family: ${({ theme }) => theme.ff.Cormorant};
        margin: 0 auto;
        max-width: 600px;
        text-align: justify;
        line-height: 160%;
    }

    @media screen and (max-width: 1050px) {
        padding: 40px 20px;
    }

    @media screen and (max-width: 830px) {
        margin: 50px 40px;
        padding: 20px 40px;
    }

    @media screen and (max-width: 730px) {
        h6 {
            font-size: ${({ theme }) => theme.fz.XXS};
        }
    }

    @media screen and (max-width: 560px) {
        width: 90%;
        margin: 0 auto;
        ${flexColumn};
        padding: 20px;
    }
`;

const DescriptionContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-align: left;
    margin-right: 50px;

    @media screen and (max-width: 560px) {
        margin: 0 auto;
        ${flexColumn};
        flex-direction: column-reverse;
    }
`;

const Title = styled.h2`
    display: inline-block;
    padding: 0 5px 4px;
    margin: 0 0 0 45px;
    line-height: 100%;
    font-weight: ${({ theme }) => theme.fw.regular};
    font-size: ${({ theme }) => theme.fz.XXL};
    text-transform: uppercase;
    letter-spacing: 3px;

    span {
        position: relative;
        right: 45px;
        font-family: ${({ theme }) => theme.ff.Cormorant};
        text-transform: lowercase;
    }

    i {
        font-size: 0.7em;
        transform: scaleX(-1);
    }

    @media screen and (max-width: 730px) {
        font-size: ${({ theme }) => theme.fz.L};
    }

    @media screen and (max-width: 560px) {
        margin: 15px auto;
        text-align: center;

        span {
            position: static;
            inset: unset;
        }
    }
`;

const Description = styled.p`
    font-family: ${({ theme }) => theme.ff.Cormorant};
    font-size: ${({ theme }) => theme.fz.S};
    width: 70%;
    max-width: 500px;
    line-height: 150%;

    @media screen and (max-width: 730px) {
        font-size: ${({ theme }) => theme.fz.XXS};
    }

    @media screen and (max-width: 560px) {
        width: 100%;
        text-align: center;
    }
`;

const InputContainer = styled.div`
    ${flexCenter};
    ${flexColumn};
    margin: 15px auto;
    position: relative;

    input[type="email"] {
        padding: 15px 30px 5px;
        border: none;
        border-bottom: 1px solid ${({ theme }) => theme.colors.black};
        font-size: ${({ theme }) => theme.fz.S};
        text-align: center;

        &::placeholder {
            font-size: ${({ theme }) => theme.fz.S};
            font-weight: ${({ theme }) => theme.fw.regular};
            color: ${({ theme }) => theme.colors.grey};
        }
    }

    button[type="submit"] {
        margin: 30px 0;
        padding: 15px 40px;
        border: 1px solid;
        border-radius: 30px;
        background-color: ${({ theme }) => theme.colors.black};
        color: ${({ theme }) => theme.colors.white};
        font-size: ${({ theme }) => theme.fz.S};
        font-weight: ${({ theme }) => theme.fw.semiBold};
        cursor: pointer;
        ${transitionMixin()};

        &:hover,
        &:focus {
            background-color: ${({ theme }) => theme.colors.pink};
            color: ${({ theme }) => theme.colors.white};
        }
    }

    @media screen and (max-width: 560px) {
        input[type="email"] {
            max-width: 80%;
        }

        button[type="submit"] {
            font-size: ${({ theme }) => theme.fz.XS};
        }
    }
`;
const NewsletterSection = (props) => {
    const [email, setEmail] = useState("");
    const [emailSent, setEmailSent] = useState(false);
    const [error, setError] = useState(null);
    const [prohibitSending, setProhibition] = useState(false);
    const [message, setMessage] = useState("");

    const messages = {
        500: "Coś poszło nie tak. Spróbuj ponownie później.",
        422: "Ten email jest już zarejestrowany!",
        201: "Twój email został zapisany. Dziękujemy że jesteś z nami!",
    };

    const handleChange = (e) => setEmail(e.target.value);

    const setRequestFailure = (statusCode) => {
        setMessage(messages[statusCode]);
        setEmailSent(false);
        setProhibition(false);
        setError(true);
    };

    const setRequestComplete = (statusCode) => {
        setMessage(messages[statusCode]);
        setEmailSent(true);
        setProhibition(true);
        setError(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setProhibition(true);
            setTimeout(() => {
                setProhibition(false);
            }, 2000);
        } catch (err) {
            setRequestFailure(500);
        }
    };

    return (
        <Container id="newsletter">
            <DescriptionContainer>
                <Description>
                    Zapisz się na nasz newsletter, a w prezencie otrzymasz
                    wydanie Zdrowego Dziecka GRATIS!
                </Description>
                <Title>
                    Zdrowy
                    <br />
                    <span>newsletter</span>
                </Title>
            </DescriptionContainer>
            {!emailSent && (
                <form autoComplete="off" onSubmit={handleSubmit}>
                    <InputContainer>
                        <input
                            type="email"
                            name="email"
                            id="email"
                            required
                            placeholder="Adres email"
                            autoComplete="off"
                            onChange={handleChange}
                        />
                        <button type="submit" disabled={prohibitSending}>
                            Zapisz się
                        </button>

                        {(() => {
                            if (emailSent || error) {
                                return (
                                    <h6
                                        className={`message ${
                                            error ? "red" : ""
                                        }`}
                                    >
                                        {message}
                                    </h6>
                                );
                            }
                        })()}
                    </InputContainer>
                </form>
            )}
            <p className="privacy-policy">
                Zapisując się na nasz newsletter, zgadzasz się, abyśmy odezwali
                się do Ciebie od czasu do czasu zgodnie z naszą{" "}
                <a href="#">Polityką prywatności.</a>
            </p>
        </Container>
    );
};
export default NewsletterSection;
